import { useState, useEffect, useCallback, useRef } from 'react'
import { useConnection } from '@/features/connection/ConnectionContext'
import { useSerialCommands } from '@/features/connection/useSerialCommands'
import { useToast } from '@/contexts/ToastContext'
import { PhotoIndexState } from '@/types/photos'

/**
 * Hook for managing photo index state
 * Automatically fetches index when connected (once)
 */
export function usePhotoIndex() {
  const { isConnected } = useConnection()
  const { getIndex, setCurrentPhoto, togglePhoto, rebuildIndex } = useSerialCommands()
  const { addToast } = useToast()

  const [state, setState] = useState<PhotoIndexState>({
    index: {},
    isLoading: false,
    error: null,
    lastUpdated: null,
  })

  // Track if we've fetched for this connection session
  const hasFetchedRef = useRef(false)
  const isFetchingRef = useRef(false)

  // Fetch index from ESP32
  const fetchIndex = useCallback(async (force = false) => {
    if (!isConnected) return
    if (isFetchingRef.current) return // Prevent concurrent fetches
    if (!force && hasFetchedRef.current) return // Already fetched this session

    isFetchingRef.current = true
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const index = await getIndex()
      hasFetchedRef.current = true
      setState({
        index: index || {},  // Fallback to empty object if null/undefined
        isLoading: false,
        error: null,
        lastUpdated: new Date(),
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch index'
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: message,
      }))
      addToast({
        type: 'error',
        title: 'Failed to load photos',
        description: message,
      })
    } finally {
      isFetchingRef.current = false
    }
  }, [isConnected, getIndex, addToast])

  // Fetch index when connection is established (only once per session)
  useEffect(() => {
    if (isConnected && !hasFetchedRef.current) {
      fetchIndex()
    } else if (!isConnected) {
      // Reset when disconnected
      hasFetchedRef.current = false
      setState({
        index: {},
        isLoading: false,
        error: null,
        lastUpdated: null,
      })
    }
  }, [isConnected]) // Intentionally not including fetchIndex to prevent re-runs

  // Set current photo for a date
  const selectPhoto = useCallback(async (date: string, photoIndex: number) => {
    try {
      await setCurrentPhoto(date, photoIndex)

      // Update local state
      setState(prev => {
        const newIndex = { ...prev.index }
        if (newIndex[date]) {
          const photos = newIndex[date].photos
          if (photos[photoIndex]) {
            newIndex[date] = {
              ...newIndex[date],
              current: photos[photoIndex].file,
            }
          }
        }
        return { ...prev, index: newIndex }
      })

      addToast({
        type: 'success',
        title: 'Photo selected',
        description: `Set as current photo for ${date}`,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to select photo'
      addToast({
        type: 'error',
        title: 'Failed to select photo',
        description: message,
      })
    }
  }, [setCurrentPhoto, addToast])

  // Toggle photo enabled/disabled
  const togglePhotoEnabled = useCallback(async (date: string, photoIndex: number) => {
    try {
      await togglePhoto(date, photoIndex)

      // Update local state
      setState(prev => {
        const newIndex = { ...prev.index }
        if (newIndex[date]?.photos[photoIndex]) {
          const photos = [...newIndex[date].photos]
          photos[photoIndex] = {
            ...photos[photoIndex],
            enabled: !photos[photoIndex].enabled,
          }
          newIndex[date] = { ...newIndex[date], photos }
        }
        return { ...prev, index: newIndex }
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to toggle photo'
      addToast({
        type: 'error',
        title: 'Failed to toggle photo',
        description: message,
      })
    }
  }, [togglePhoto, addToast])

  // Rebuild index from SD card files
  const rebuild = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }))

    try {
      await rebuildIndex()
      await fetchIndex(true) // Force refetch

      addToast({
        type: 'success',
        title: 'Index rebuilt',
        description: 'Photo index has been rebuilt from SD card',
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to rebuild index'
      setState(prev => ({ ...prev, isLoading: false, error: message }))
      addToast({
        type: 'error',
        title: 'Failed to rebuild index',
        description: message,
      })
    }
  }, [rebuildIndex, fetchIndex, addToast])

  // Get photos for a specific date
  const getPhotosForDate = useCallback((date: string) => {
    if (!state.index) return []
    return state.index[date]?.photos || []
  }, [state.index])

  // Get current photo for a date
  const getCurrentPhoto = useCallback((date: string) => {
    if (!state.index) return null
    return state.index[date]?.current || null
  }, [state.index])

  // Check if a date has any photos
  const hasPhotos = useCallback((date: string) => {
    if (!state.index) return false
    const entry = state.index[date]
    return entry?.photos?.length > 0
  }, [state.index])

  // Get all dates that have photos
  const getDatesWithPhotos = useCallback(() => {
    if (!state.index) return []
    return Object.keys(state.index).filter(date => state.index[date]?.photos?.length > 0)
  }, [state.index])

  // Check if a filename already exists in the index
  const checkDuplicate = useCallback((filename: string): { exists: boolean; date?: string } => {
    if (!state.index) return { exists: false }
    // Search through all dates for the filename
    for (const date of Object.keys(state.index)) {
      const photos = state.index[date]?.photos || []
      if (photos.some(p => p.file === filename)) {
        return { exists: true, date }
      }
    }
    return { exists: false }
  }, [state.index])

  // Get all filenames in the index
  const getAllFilenames = useCallback(() => {
    if (!state.index) return []
    const filenames: string[] = []
    for (const date of Object.keys(state.index)) {
      const photos = state.index[date]?.photos || []
      filenames.push(...photos.map(p => p.file))
    }
    return filenames
  }, [state.index])

  // Manual refresh (forces refetch)
  const refresh = useCallback(() => {
    return fetchIndex(true)
  }, [fetchIndex])

  return {
    index: state.index,
    isLoading: state.isLoading,
    error: state.error,
    lastUpdated: state.lastUpdated,
    fetchIndex: refresh, // Expose as refresh that forces
    selectPhoto,
    togglePhotoEnabled,
    rebuild,
    getPhotosForDate,
    getCurrentPhoto,
    hasPhotos,
    getDatesWithPhotos,
    checkDuplicate,
    getAllFilenames,
  }
}
